import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

export type GenieCategoryId =
  | "customer-support"
  | "sales-marketing"
  | "internal-ops"
  | "personal-productivity"
  | "education-training"
  | "event-hospitality"
  | "custom-creative";

export interface GenieCapabilityCategory {
  id: GenieCategoryId;
  title: string;
  description: string;
  tags?: string[];
  genieIcon?: GenieIconName;
  accentColor?: string;
  example?: {
    title: string;
    summary: string;
    ctaLabel: string;
    ctaHref?: string;
  };
  share?: {
    slug: string;
    phone?: string;
  };
}

export const GENIE_CAPABILITY_CATEGORIES: GenieCapabilityCategory[] = [
  {
    id: "customer-support",
    title: "Customer Support Genies",
    description: "Handle FAQs, troubleshoot, provide status updates, escalate issues.",
    tags: [
      "FAQs",
      "Troubleshooting",
      "Status updates",
      "Escalations",
      "Order tracking",
      "Returns & refunds",
      "Account help",
      "Ticket creation",
    ],
    genieIcon: "clapperboard",
    accentColor: "#3b82f6",
    example: {
      title: "24/7 Support Concierge",
      summary: "Answers account and order questions and creates tickets when needed.",
      ctaLabel: "View example",
    },
    share: {
      slug: "customer-support-genie",
      phone: "+18723168076",
    },
  },
  {
    id: "sales-marketing",
    title: "Sales & Marketing Genies",
    description: "Lead qualification, product explainers, event promotions, campaigns.",
    tags: [
      "Lead qual",
      "Explainers",
      "Promotions",
      "Campaigns",
      "Demos",
      "Upsell/Cross-sell",
      "Event signup",
      "Newsletter opt-in",
    ],
    genieIcon: "database",
    accentColor: "#1e40af",
    example: {
      title: "Product Explainer",
      summary: "Qualifies interest and routes hot leads to sales instantly.",
      ctaLabel: "View example",
    },
    share: {
      slug: "sales-and-marketing-genies",
      phone: "+17754149263",
    },
  },
  {
    id: "internal-ops",
    title: "Internal Ops Genies",
    description: "HR assistants, IT helpdesk, onboarding guides, compliance checks.",
    tags: [
      "HR",
      "IT helpdesk",
      "Onboarding",
      "Compliance",
      "Policy lookup",
      "Time-off",
      "Approval flows",
      "Knowledge lookup",
    ],
    genieIcon: "genie-lamp",
    accentColor: "#D6409F",
    example: {
      title: "IT Helpdesk",
      summary: "Troubleshoots common issues and escalates to the right queue.",
      ctaLabel: "View example",
    },
    share: {
      slug: "the-internal-ops-genie",
      phone: "+14195533310",
    },
  },
  {
    id: "personal-productivity",
    title: "Personal Productivity Genies",
    description: "Scheduling, reminders, task management, personal Q&A.",
    tags: [
      "Scheduling",
      "Reminders",
      "Tasks",
      "Personal Q&A",
      "Note taking",
      "Summaries",
      "Habit tracking",
      "Calendar assist",
    ],
    genieIcon: "genie-lamp",
    accentColor: "#4E9CFF",
    example: {
      title: "Scheduling Assistant",
      summary: "Finds times, drafts invites, and confirms with attendees.",
      ctaLabel: "View example",
    },
    share: {
      slug: "personal-productivity-genie",
      phone: "+17173668178",
    },
  },
  {
    id: "education-training",
    title: "Education & Training Genies",
    description: "Tutors, onboarding coaches, skill trainers, explainer bots.",
    tags: [
      "Tutors",
      "Onboarding",
      "Skill training",
      "Explainers",
      "Quizzes",
      "Course helper",
      "Cert prep",
      "Microlearning",
    ],
    genieIcon: "book-open",
    accentColor: "#10b981",
    example: {
      title: "Onboarding Coach",
      summary: "Guides new users through the first seven days to activation.",
      ctaLabel: "View example",
    },
    share: {
      slug: "education-and-training-genie",
      phone: "+19123015195",
    },
  },
  {
    id: "event-hospitality",
    title: "Event & Hospitality Genies",
    description: "Venue guides, booking assistants, concierge, tour guides.",
    tags: [
      "Venue guides",
      "Bookings",
      "Concierge",
      "Tours",
      "Availability",
      "Check-in",
      "Recommendations",
      "Event schedule",
    ],
    genieIcon: "globe",
    accentColor: "#f59e0b",
    example: {
      title: "Booking Assistant",
      summary: "Answers availability questions and captures reservations.",
      ctaLabel: "View example",
    },
    share: {
      slug: "event-and-hospitality-genie",
      phone: "+18722968317",
    },
  },
  {
    id: "custom-creative",
    title: "Custom/Creative Genies",
    description: "Anything experimental — characters, branded personalities, entertainment.",
    tags: [
      "Characters",
      "Branded",
      "Entertainment",
      "Experimental",
      "Storytellers",
      "Campaign mascots",
      "Games",
      "Voice personas",
    ],
    genieIcon: "mail",
    accentColor: "#8b5cf6",
    example: {
      title: "Branded Character",
      summary: "A playful persona for campaigns and interactive content.",
      ctaLabel: "View example",
    },
    share: {
      slug: "custom-and-creative-genie",
      phone: "+15513735301",
    },
  },
];

export default GENIE_CAPABILITY_CATEGORIES;
